import React, { useEffect, useState } from 'react';
import { managerAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { FileText, AlertCircle, Loader2, Calendar, User, Plus, Pencil, Trash2, Download, Search, X } from 'lucide-react';
import { toast } from 'sonner';

export default function ManagerLeases() {
  const [leases, setLeases] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLease, setEditingLease] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState({ tenant_id: '', unit_id: '', start_date: '', end_date: '', monthly_rent: 0, base_rent: 0, deposit: 0, terms: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [leasesRes, dashRes] = await Promise.all([
        managerAPI.getLeases(),
        managerAPI.getDashboard()
      ]);
      setLeases(leasesRes.data);
      setPermissions(dashRes.data.permissions || {});
      // Load units and tenants for the create form
      try {
        const [unitsRes, tenantsRes] = await Promise.all([
          managerAPI.getUnits(),
          managerAPI.getTenants()
        ]);
        setUnits(unitsRes.data);
        setTenants(tenantsRes.data);
      } catch (err) {
        console.error('Failed to load supplementary data:', err);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load leases');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingLease(null);
    const today = new Date().toISOString().split('T')[0];
    const nextYear = new Date(Date.now() + 365 * 86400000).toISOString().split('T')[0];
    setForm({ tenant_id: '', unit_id: '', start_date: today, end_date: nextYear, monthly_rent: 0, base_rent: 0, deposit: 0, terms: '' });
    setDialogOpen(true);
  };

  const openEdit = (lease) => {
    setEditingLease(lease);
    setForm({
      tenant_id: lease.tenant_id || '',
      unit_id: lease.unit_id || '',
      start_date: lease.start_date?.split('T')[0] || '',
      end_date: lease.end_date?.split('T')[0] || '',
      monthly_rent: lease.monthly_rent || 0,
      base_rent: lease.base_rent || lease.monthly_rent || 0,
      deposit: lease.deposit || 0,
      terms: lease.terms || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.tenant_id || !form.unit_id || !form.start_date || !form.end_date) {
      toast.error('Tenant, unit, start and end dates are required');
      return;
    }
    setSaving(true);
    const payload = { ...form, monthly_rent: Number(form.monthly_rent), base_rent: Number(form.base_rent), deposit: Number(form.deposit) };
    try {
      if (editingLease) {
        await managerAPI.updateLease(editingLease.lease_id, payload);
        toast.success('Lease updated');
      } else {
        await managerAPI.createLease(payload);
        toast.success('Lease created');
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save lease');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (lease) => {
    if (!window.confirm(`Delete this lease for ${lease.tenant?.name || 'Unknown'}? This cannot be undone.`)) return;
    try {
      await managerAPI.deleteLease(lease.lease_id);
      toast.success('Lease deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete lease');
    }
  };

  const handleDownloadPdf = async (lease) => {
    try {
      const res = await managerAPI.downloadLeasePdf(lease.lease_id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `lease_${lease.lease_id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      expired: 'bg-gray-100 text-gray-800',
      terminated: 'bg-red-100 text-red-800',
    };
    return <Badge className={styles[status] || styles.pending}>{status?.charAt(0).toUpperCase() + status?.slice(1)}</Badge>;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString() : 'N/A';

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#C85A42]" /></div>;
  if (error) return <div className="flex flex-col items-center justify-center h-64 text-center"><AlertCircle className="w-12 h-12 text-red-500 mb-4" /><h2 className="text-xl font-semibold mb-2">Access Error</h2><p className="text-muted-foreground">{error}</p></div>;

  const vacantUnits = units.filter(u => u.status === 'vacant');
  const availableTenants = tenants.filter(t => t.lease_status === 'available' || t.lease_status === 'former');

  // Client-side filter: search + status
  const query = search.trim().toLowerCase();
  const filteredLeases = leases.filter((lease) => {
    if (statusFilter !== 'all' && lease.status !== statusFilter) return false;
    if (!query) return true;
    const hay = [
      lease.tenant?.name, lease.tenant?.email,
      lease.property?.name, lease.unit?.unit_number,
      lease.status, lease.monthly_rent,
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(query);
  });

  return (
    <div className="space-y-6" data-testid="manager-leases-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Leases</h1>
          <p className="text-muted-foreground">All leases in your assigned properties</p>
        </div>
        {permissions.create_leases && (
          <Button onClick={openCreate} data-testid="create-lease-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Lease
          </Button>
        )}
      </div>

      {leases.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by tenant, property, unit…"
              className="pl-9 pr-9"
              data-testid="manager-leases-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid="manager-leases-search-clear"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-44" data-testid="manager-leases-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
              <SelectItem value="terminated">Terminated</SelectItem>
            </SelectContent>
          </Select>
          {(search || statusFilter !== 'all') && (
            <div className="text-sm text-muted-foreground whitespace-nowrap" data-testid="manager-leases-result-count">
              {filteredLeases.length} of {leases.length}
            </div>
          )}
        </div>
      )}

      {leases.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold text-lg mb-2">No Leases Found</h3><p className="text-muted-foreground">There are no leases in your assigned properties yet.</p></CardContent></Card>
      ) : filteredLeases.length === 0 ? (
        <Card><CardContent className="p-12 text-center"><Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><h3 className="font-semibold text-lg mb-2">No matches</h3><p className="text-muted-foreground">No leases match your search.</p></CardContent></Card>
      ) : (
        <Card>
          <CardHeader><CardTitle>All Leases</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Property / Unit</TableHead>
                  <TableHead>Rent</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeases.map((lease) => (
                  <TableRow key={lease.lease_id}>
                    <TableCell>
                      <div className="flex items-center gap-2"><User className="w-4 h-4 text-muted-foreground" /><span className="font-medium">{lease.tenant?.name || 'Unknown'}</span></div>
                    </TableCell>
                    <TableCell>
                      <div><div className="font-medium">{lease.property?.name || 'Unknown'}</div><div className="text-sm text-muted-foreground">Unit {lease.unit?.unit_number || 'N/A'}</div></div>
                    </TableCell>
                    <TableCell>{lease.monthly_rent?.toLocaleString()}</TableCell>
                    <TableCell><div className="flex items-center gap-1"><Calendar className="w-4 h-4 text-muted-foreground" />{formatDate(lease.start_date)}</div></TableCell>
                    <TableCell>{formatDate(lease.end_date)}</TableCell>
                    <TableCell>{getStatusBadge(lease.status)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(lease)} data-testid={`download-lease-${lease.lease_id}`}><Download className="w-4 h-4" /></Button>
                        {permissions.edit_leases && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => openEdit(lease)} data-testid={`edit-lease-${lease.lease_id}`}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(lease)} data-testid={`delete-lease-${lease.lease_id}`}><Trash2 className="w-4 h-4" /></Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingLease ? 'Edit Lease' : 'Create Lease'}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {!editingLease && (
              <>
                <div>
                  <Label>Tenant *</Label>
                  <Select value={form.tenant_id} onValueChange={v => setForm({ ...form, tenant_id: v })}>
                    <SelectTrigger data-testid="lease-tenant-select"><SelectValue placeholder="Select tenant" /></SelectTrigger>
                    <SelectContent>{availableTenants.map(t => <SelectItem key={t.user_id} value={t.user_id}>{t.name} ({t.email || 'No email'})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unit *</Label>
                  <Select value={form.unit_id} onValueChange={v => {
                    const unit = units.find(u => u.unit_id === v);
                    setForm({ ...form, unit_id: v, monthly_rent: unit?.monthly_rent || form.monthly_rent, base_rent: unit?.monthly_rent || form.base_rent });
                  }}>
                    <SelectTrigger data-testid="lease-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                    <SelectContent>{vacantUnits.map(u => <SelectItem key={u.unit_id} value={u.unit_id}>{u.unit_number} - {u.property?.name || 'Unknown'} ({u.monthly_rent?.toLocaleString()})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Date *</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} data-testid="lease-start-date" /></div>
              <div><Label>End Date *</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} data-testid="lease-end-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Monthly Rent</Label><Input type="number" value={form.monthly_rent} onChange={e => setForm({ ...form, monthly_rent: e.target.value })} /></div>
              <div><Label>Deposit</Label><Input type="number" value={form.deposit} onChange={e => setForm({ ...form, deposit: e.target.value })} /></div>
            </div>
            <div><Label>Terms</Label><Input value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} placeholder="Lease terms or notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-lease-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{editingLease ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
